import { PlatformComponentKey, PlatformStatusDetail } from '@models/platformStatus.model';
import {
  platformStatusService,
  PublishMessagePayload,
  SetOverridePayload,
} from '@services/platformStatus';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@utils/errorMessage';

export const PLATFORM_STATUS_DETAIL_QUERY_KEY = ['platformStatusDetail'];

export const usePlatformStatusDetail = () =>
  useQuery<PlatformStatusDetail, Error>({
    queryKey: PLATFORM_STATUS_DETAIL_QUERY_KEY,
    queryFn: () => platformStatusService.getPlatformStatusDetail(),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

/**
 * The detail query is invalidated after every write.
 *
 * It backs both the table and the live preview on this screen, so leaving it
 * cached would show a stale "before" beside a fresh "after" — and the moment
 * right after publishing an outage is the worst time to make someone wonder
 * whether it took.
 */
const useStatusMutation = <T>(
  mutationFn: (payload: T) => Promise<void>,
  successMessage: string,
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, T>({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLATFORM_STATUS_DETAIL_QUERY_KEY });
      toast.success(successMessage);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
};

export const useSetPlatformStatusOverride = () =>
  useStatusMutation<SetOverridePayload>(
    (payload) => platformStatusService.setOverride(payload),
    'Status override published.',
  );

export const useClearPlatformStatusOverride = () =>
  useStatusMutation<PlatformComponentKey>(
    (componentKey) => platformStatusService.clearOverride(componentKey),
    'Override cleared — the component is back on automatic checks.',
  );

export const usePublishPlatformStatusMessage = () =>
  useStatusMutation<PublishMessagePayload>(
    (payload) => platformStatusService.publishMessage(payload),
    'Message published to the public status page.',
  );

export const useClearPlatformStatusMessage = () =>
  useStatusMutation<void>(
    () => platformStatusService.clearMessage(),
    'Message removed from the public status page.',
  );
